import { Injectable, } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  ImplementerEditor,
  ImplementerOperationFailure,
  ImplementerSnapshotSummary
} from '../../models/implementer/implementer.models';
import { PtLuxonDate } from '../../models/projects/date.models';
import { cloneDeep } from 'lodash';
import { CompleteDayEditor } from '../../models/complete-day.models';
import { NavHelperService } from '../nav/nav-helper.service';
import { AppConfig } from '../../app/app.config';
import { ImplementerDateService } from './implementer-date.service';
import { DateParserService } from '../daily-log/daily-log-time/date-parser-service';
import { ValidationResultModel } from '../../_foundation/models/validation.models';

@Injectable()
export class ImplementerService {

  config = AppConfig.settings;

  constructor(
    private http: HttpClient,
    private navHelperService: NavHelperService,
    private implementerDateService: ImplementerDateService,
    private dateParserService: DateParserService
  ) {

  }

  async getActiveSnapshotSummary(isVessel: boolean): Promise<ImplementerSnapshotSummary> {
    const url = `${this.config.apiUrl}api/implementer/getactivesnapshotsummary/${isVessel}`;
    const res = await this.http.get<ImplementerSnapshotSummary>(url).toPromise();
    return await this.implementerDateService.setImplementerSnapshotSummaryLocalDates(res);
  }

  async getLatestSnapshotSummary(isVessel: boolean): Promise<ImplementerSnapshotSummary> {
    const url = `${this.config.apiUrl}api/implementer/getlatestsnapshotsummary/${isVessel}`;
    const res = await this.http.get<ImplementerSnapshotSummary>(url).toPromise();
    return await this.implementerDateService.setImplementerSnapshotSummaryLocalDates(res);
  }

  async implementerIsActive(isVessel: boolean, dailyLogDate: PtLuxonDate): Promise<boolean> {
    const dateLong = dailyLogDate.dateTime.toMillis();
    const url = `${this.config.apiUrl}api/implementer/${isVessel}/${dateLong}/isactive`;
    return await this.http.get<boolean>(url).toPromise();
  }

  async getImplementerEditor(isVessel: boolean): Promise<ImplementerEditor> {
    const url = `${this.config.apiUrl}api/implementer/geteditor/${isVessel}`;
    const res = await this.http.get<ImplementerEditor>(url).toPromise();
    return await this.implementerDateService.setImplementerEditorLocalDates(res);
  }

  async getImplementerCompleteDayEditors(isVessel: boolean): Promise<CompleteDayEditor[]> {
    const url = `${this.config.apiUrl}api/implementer/getimplementercompletedayeditors/${isVessel}`;
    const res = await this.http.get<CompleteDayEditor[]>(url).toPromise();
    return await this.implementerDateService.setCompleteDayEditorLocalDates(res);
  }

  async activateImplementer(editor: ImplementerEditor): Promise<ImplementerSnapshotSummary> {
    const url = `${this.config.apiUrl}api/implementer/restorevesseltoimplementer`;
    const editorClone = this.dateParserService.removeOffsetFromLuxonDates(cloneDeep(editor));
    const res = await this.http.post<any>(url, editorClone).toPromise();
    return await this.implementerDateService.setImplementerSnapshotSummaryLocalDates(res);
  }

  async mergeImplementerChanges(isVessel: boolean) {
    const url = `${this.config.apiUrl}api/implementer/mergeimplementerchanges/${isVessel}`;
    return await this.http.get(url).toPromise();
  }

  async cancelImplementerChanges(isVessel: boolean): Promise<ImplementerSnapshotSummary> {
    const url = `${this.config.apiUrl}api/implementer/cancelimplementerchanges/${isVessel}`;
    const res = await this.http.get<ImplementerSnapshotSummary>(url).toPromise();
    return await this.implementerDateService.setImplementerSnapshotSummaryLocalDates(res);
  }

  // tries to connect with alt environment directly instead of having to wait for replication to run
  async getAlternateSnapshotDirectly(isVessel: boolean): Promise<ImplementerSnapshotSummary> {
    const url = `${this.config.apiUrl}api/implementer/${isVessel}/trygetsnapshotforaltenvironment`;
    const res = await this.http.get<ImplementerSnapshotSummary>(url).toPromise();
    return await this.implementerDateService.setImplementerSnapshotSummaryLocalDates(res);
  }

  async checkJackingFormsModifiedSinceDate(implementerCreationTime: PtLuxonDate): Promise<boolean> {
    const url = `${this.config.apiUrl}api/implementer/checkjackingformsmodifiedsincedate/${implementerCreationTime.dateTime.toMillis()}`;
    return await this.http.get<boolean>(url).toPromise();
  }

  async checkTimeZoneChangeDetectedinceImplementerStart(): Promise<boolean> {
    const url = `${this.config.apiUrl}api/implementer/timezone/check/modified`;
    return await this.http.get<boolean>(url).toPromise();
  }

  getFailure(error: any): ImplementerOperationFailure {
    const errorBody = error && error.error ? error.error : error;

    if (!errorBody || errorBody.isValid !== false || !Array.isArray(errorBody.errors) || errorBody.errors.length === 0) {
      return null;
    }

    return errorBody as ImplementerOperationFailure;
  }

  buildFailureValidationResult(error: any, fallbackMessage: string, includeStageInMessage: boolean = true): ValidationResultModel {
    const validationResult = new ValidationResultModel();
    validationResult.isValid = false;
    validationResult.errors = [];

    const failure = this.getFailure(error);
    if (failure) {
      const primaryError = failure.errors[0];
      const stageSuffix = includeStageInMessage && failure.stage ? ` (Stage: ${failure.stage})` : '';

      validationResult.errors.push({
        message: `${primaryError.message}${stageSuffix}`,
        property: null
      });

      console.error('Implementer operation failed', {
        operation: failure.operation,
        stage: failure.stage,
        statusCode: failure.statusCode,
        errorMessage: primaryError.errorMessage
      });

      return validationResult;
    }

    validationResult.errors.push({
      message: fallbackMessage,
      property: null
    });

    return validationResult;
  }
}



// WEBPACK FOOTER //
// ./src/services/implementer/implementer.service.ts